import { useState } from 'react'

import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import type { FilterOption } from '#components/adminFilters/types.js'
import { ReportDataWrapper } from '#components/ReportDataWrapper'
import { useFetch } from '#hooks/useAdminResource'

interface QueueDateBucket {
  date: string
  count: number
}

interface QueueStatusBucket {
  status: string
  count: number
}

interface QueueCarrierDetailData {
  carrierCode: string
  total: number
  pickupDates: QueueDateBucket[]
  pickupDatesUnknown: number
  deliveryDates?: QueueDateBucket[]
  deliveryDatesUnknown?: number
  statuses: QueueStatusBucket[]
}

type BucketType = 'pickup' | 'delivery' | 'status'

const pickupOption: FilterOption = { value: 'pickup', label: 'Pickup Date' }
const deliveryOption: FilterOption = {
  value: 'delivery',
  label: 'Actual Delivery',
}
const statusOption: FilterOption = { value: 'status', label: 'Status' }

interface BucketRow {
  label: string
  count: number
  muted?: boolean
}

const toBucketRows = (
  data: QueueCarrierDetailData,
  bucket: BucketType,
): { header: string; rows: BucketRow[] } => {
  if (bucket === 'status') {
    return {
      header: 'Status',
      rows: data.statuses.map(item => ({
        label: item.status,
        count: item.count,
      })),
    }
  }

  const isPickup = bucket === 'pickup'
  const dates = (isPickup ? data.pickupDates : data.deliveryDates) ?? []
  const unknown =
    (isPickup ? data.pickupDatesUnknown : data.deliveryDatesUnknown) ?? 0

  return {
    header: 'Date',
    rows: [
      ...dates.map(item => ({ label: item.date, count: item.count })),
      ...(unknown > 0
        ? [
            {
              label: isPickup ? 'No pickup date' : 'Not delivered',
              count: unknown,
              muted: true,
            },
          ]
        : []),
    ],
  }
}

/** Per-carrier breakdown served from `${endpoint}/${carrierCode}` */
const QueueCarrierDetail = ({
  endpoint,
  carrierCode,
}: {
  endpoint: string
  carrierCode: string
}) => {
  const [bucket, setBucket] = useState<BucketType>('pickup')
  const {
    data,
    isLoading,
    error,
    refetch: refresh,
  } = useFetch<QueueCarrierDetailData>(`${endpoint}/${carrierCode}`, {})

  const { header, rows } = data
    ? toBucketRows(data, bucket)
    : { header: '', rows: [] }

  const bucketOptions = [
    pickupOption,
    ...(data?.deliveryDates ? [deliveryOption] : []),
    statusOption,
  ]

  return (
    <ReportDataWrapper
      isLoading={isLoading}
      error={error}
      loadingMessage={`Loading ${carrierCode.toUpperCase()} breakdown...`}
      onRetry={() => refresh()}
    >
      {data && (
        <div className="flex flex-col gap-4">
          <div className="flex items-end justify-between gap-4">
            <FilterSelect
              label="Breakdown"
              value={bucket}
              onChange={value => setBucket(value as BucketType)}
              options={bucketOptions}
              className="w-64"
            />
            <span className="pb-3 text-sm opacity-60">
              {data.total.toLocaleString()} queued
            </span>
          </div>

          <div className="max-h-[calc(100vh-18rem)] overflow-y-auto">
            <table className="table table-zebra table-pin-rows table-sm">
              <thead>
                <tr>
                  <th>{header}</th>
                  <th className="text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.label} className={row.muted ? 'opacity-60' : ''}>
                    <td>{row.label}</td>
                    <td className="text-right">{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportDataWrapper>
  )
}

export { QueueCarrierDetail }
